import { Button, Space, Tag, theme, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';
import { Routes } from '../../../../app/routes';
import { ExpandTableParentIcon } from '../../../../shared/components/expand-table-parent-icon/expand-table-parent-icon';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import Table from '../../../../shared/components/table/table';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { DocumentAddIcon } from '../../../../shared/icons/document-add-icon';
import { DocumentImagePlusIcon } from '../../../../shared/icons/document-image-plus-icon';
import { ScanRequestTableModel } from '../../../../shared/models/scan-request-table-model';
import { useAppSelector } from '../../../../store/hooks';
import styles from '../../../../styles/components/scan-sign-requests-table.module.css';
import { selectActiveRequestId, setActiveRequestId } from '../../../request/store/requests-slice';
import { signRequestCreateModalId } from '../../../sign-requests/constants/constants';
import { openModal } from '../../../ui/store/ui-slice';
import { useGetUserQuery } from '../../../user/api/user-api';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { scanRequestFormModalId } from '../../constants/constants';
import {
  selectActiveScanRequestId,
  selectIsScanRequestPreviewOpen,
  selectSelectedScanRequestIds,
  setActiveScanRequestId,
  setIsScanRequestPreviewOpen,
  setSelectedScanRequestIds,
  setTempScanRequestId,
} from '../../store/scan-requests-slice';

interface Props {
  isFetchingScanRequests: boolean;
  scanRequestsDataSource: ScanRequestTableModel[];
}
const { useToken } = theme;

const ScanRequestsTable = ({ isFetchingScanRequests, scanRequestsDataSource }: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'scan-requests']);
  const activeScanRequestId = useAppSelector(selectActiveScanRequestId);
  const activeRequestId = useAppSelector(selectActiveRequestId);
  const isScanRequestPreviewOpen = useAppSelector(selectIsScanRequestPreviewOpen);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedUnarchivedRequests = useAppSelector(selectSelectedScanRequestIds);
  const { token } = useToken();
  // it is needed to use useMediaQuery because antd breakpoint hook returns real token values.
  const matchesScreenXXL = useMediaQuery(`(min-width: ${token.screenXXL}px)`);
  const location = useLocation();
  const { data: currentUser } = useGetUserQuery();

  const columns: ColumnsType<ScanRequestTableModel> = [
    {
      dataIndex: 'expand',
      key: 'expand',
      className: 'text-nowrap',
      width: 70,
    },
    {
      title: t('label.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('label.user'),
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: t('label.customer'),
      dataIndex: 'customer',
      key: 'customer',
      hidden: !(currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers)),
    },
    {
      title: t('label.unit'),
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: t('label.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value, record) => (
        <Tag className="text-wrap-auto" color={record.status === 'scanned' ? 'success' : 'default'}>
          {record.status === 'scanned'
            ? t('statusOption.scanned', { ns: 'scan-requests' })
            : record.status === 'pending' && t('signScanRequest.label.statusOption.pending')}
        </Tag>
      ),
    },
    {
      title: t('signScanRequest.label.scannedAt'),
      dataIndex: 'scannedAt',
      key: 'scannedAt',
      className: 'text-break-normal',
    },
    {
      title: t('signScanRequest.label.uploadToServerStatus'),
      dataIndex: 'archived',
      key: 'archived',
      className: 'text-break-normal',
      render: (value, record) =>
        record.canArchive ? (
          <Tag className="text-wrap-auto" color={record.archived ? 'success' : 'default'}>
            {record.archived ? t('signScanRequest.label.uploaded') : t('signScanRequest.label.uploadFailed')}
          </Tag>
        ) : (
          <Tag color="default">N/A</Tag>
        ),
    },
    {
      title: t('signScanRequest.label.relatedSignRequestCount'),
      dataIndex: 'signRequestCount',
      key: 'signRequestCount',
      className: 'text-break-normal',
    },
    {
      title: t('signScanRequest.label.relatedScanRequestCount'),
      dataIndex: 'scanRequestCount',
      key: 'scanRequestCount',
      className: 'text-break-normal',
    },
    {
      title: t('label.actions'),
      key: 'actions',
      fixed: 'right',
      width: 168,
      render: record => (
        <Space size="middle">
          <Tooltip mouseEnterDelay={0.5} title={t('signScanRequest.action.scanDocument')}>
            <Button
              onClick={event => {
                event.stopPropagation();
                dispatch(
                  openModal({
                    id: scanRequestFormModalId,
                    props: { requestId: record.request.id },
                  }),
                );
              }}
              type="text"
              shape="circle"
              disabled={record.isDeleted}
              icon={
                <IconWrapper
                  textProps={{
                    type: 'success',
                  }}
                >
                  <DocumentAddIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('action.history')}>
            <Link to={Routes.ScanRequestTraceLogs.replace(':id', record.key.split('_')[0])}>
              <Button
                onClick={event => event.stopPropagation()}
                type="text"
                shape="circle"
                icon={<ClockHistoryIcon />}
              />
            </Link>
          </Tooltip>
          <Tooltip mouseEnterDelay={0.5} title={t('signScanRequest.action.additionalDocument')}>
            <Button
              onClick={event => {
                event.stopPropagation();

                dispatch(
                  openModal({
                    id: signRequestCreateModalId,
                    props: { requestId: record.request.id },
                  }),
                );
              }}
              type="text"
              shape="circle"
              disabled={record.isDeleted}
              icon={
                <IconWrapper>
                  <DocumentImagePlusIcon />
                </IconWrapper>
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!matchesScreenXXL && activeRequestId && activeScanRequestId && isScanRequestPreviewOpen) {
      navigate(
        Routes.ScanRequest.replace(':scanRequestId', activeScanRequestId).replace(':requestId', activeRequestId),
        { state: { from: location } },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRequestId, activeScanRequestId, isScanRequestPreviewOpen, matchesScreenXXL]);

  const handleOnScanRequestRowClick = (record: ScanRequestTableModel, shouldTogglePreview = false) => {
    dispatch(setTempScanRequestId(null));

    if (!record.children) {
      dispatch(setTempScanRequestId(record.id));
      dispatch(setIsScanRequestPreviewOpen(true));
      return;
    }

    if (activeScanRequestId === record.id) {
      if (!isScanRequestPreviewOpen && shouldTogglePreview) {
        dispatch(setIsScanRequestPreviewOpen(true));
        return;
      }

      dispatch(setActiveRequestId(null));
      dispatch(setActiveScanRequestId(null));
      dispatch(setTempScanRequestId(null));
      dispatch(setIsScanRequestPreviewOpen(false));
      return;
    }

    dispatch(setActiveRequestId(record.request.id));
    dispatch(setActiveScanRequestId(record.id));

    if (shouldTogglePreview) {
      dispatch(setIsScanRequestPreviewOpen(true));
    }
  };

  return (
    <div className={styles.tableContainer}>
      <Table
        tableProps={{
          rowClassName: record =>
            clsx({
              [styles.requestIsDeleted]: record.isDeleted,
              [styles.clickableRow]: true,
              [styles.activeRow]: record.id === activeScanRequestId,
            }),
          scroll: {
            x: activeScanRequestId ? 1200 : 2000,
            y: 'calc(100vh - 160px)',
          },
          columns: columns,
          dataSource: scanRequestsDataSource,
          onRow: (record: ScanRequestTableModel) => ({
            onClick: () => handleOnScanRequestRowClick(record, true),
          }),
          loading: isFetchingScanRequests,
          rowSelection: {
            type: 'checkbox',
            selectedRowKeys: selectedUnarchivedRequests || [],
            onSelect: (record, selected, selectedRows) => {
              const transformedSelectedRows = selectedRows.map(row => row.id);
              dispatch(setSelectedScanRequestIds(transformedSelectedRows));
            },
            onChange: selectedRowKeys => dispatch(setSelectedScanRequestIds(selectedRowKeys as string[])),
            preserveSelectedRowKeys: true,
          },
          expandable: {
            indentSize: 0,
            expandedRowKeys: activeScanRequestId ? [activeScanRequestId] : [],
            expandIcon: ({ expanded, record }) =>
              record.children && record.scanRequestCount > 1 ? (
                <ExpandTableParentIcon
                  expanded={expanded}
                  onClick={event => {
                    event.stopPropagation();
                    handleOnScanRequestRowClick(record);
                  }}
                />
              ) : undefined,
          },
          pagination: false,
        }}
      />
    </div>
  );
};

export default ScanRequestsTable;
